import { Component, Input } from '@angular/core';
import { agreementScheduledPayments } from '../agreement-scheduled-payments';

@Component({
  selector: 'app-agreement-template-italian',
  templateUrl: './agreement-template-italian.component.html',
  styleUrls: ['./agreement-template-italian.component.css']
})
export class AgreementTemplateItalianComponent {
  @Input() entityData: any;
  @Input() studentAccountEntityData: any;
  @Input() studioInformationConfig: any;
  @Input() enrollmentForm: any;
  @Input() isTaxInclusive = false;
  @Input() displaySignatures = true;
  @Input() _enrollmentEntityData: any;

  get primaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!contacts) return null;
    if (Array.isArray(contacts)) {
      const active = contacts.filter(c => c.field_archived !== '1' && c.field_archived !== 1);
      return active[0] || contacts[0] || null;
    }
    return contacts;
  }

  calculateTotalTuition(): number {
    return this.entityData?.field_enrollment_total_price || 0;
  }

  getOriginalPackageCost(): number {
    if (this.entityData?.field_enrollment_lesson_price && this.entityData?.field_enrollment_lesson_count) {
      return this.entityData.field_enrollment_lesson_price * this.entityData.field_enrollment_lesson_count;
    }
    return 0;
  }

  /**
   * Section B (Modalità di Pagamento) fill-in, derived from the scheduled
   * payment plan. Maps AMT structures onto the paper form's lines:
   *   by_the_lesson → a) € X ogni N lezioni a partire da {date}
   *   weekly        → b) € X ogni 7 giorni a partire da {date}
   *   monthly       → b) € X ogni mese a partire da {date}
   *   2 installments → c) 1ª/2ª RATA
   *   d) saldo il {last payment date}, always when known
   * Returns null when no plan exists yet (lines stay blank, like the paper form).
   */
  get sectionB(): any {
    const rows = this.scheduledPaymentRows();
    if (!rows.length) return null;

    const structureType = this.formOrEntityValue('field_payments_structure_type');
    // By-the-lesson generation flags its FIRST per-lessons payment as a "down
    // payment" (enrollment-entity.component.ts). It's not a separate acconto,
    // so treat every row as an installment for that structure.
    const isByLesson = structureType === 'by_the_lesson';
    const installments = isByLesson ? rows : rows.filter(r => !r.down);
    const downRow = isByLesson ? null : (rows.find(r => r.down) || null);
    const first = installments[0] || rows[0];
    const last = installments[installments.length - 1] || rows[rows.length - 1];

    let line: 'a' | 'b' | 'c' | null = null;
    let lessonsPerPayment: number | null = null;
    let every: string | null = null;

    if (installments.length <= 1) {
      // Single payment (paid in full / down payment covers it): only d) fills.
      line = null;
    } else if (structureType === 'by_the_lesson') {
      line = 'a';
      lessonsPerPayment = this.byLessonInterval(installments.length);
    } else if (installments.length === 2) {
      line = 'c';
    } else if (structureType === 'weekly' || structureType === 'monthly') {
      line = 'b';
      every = structureType === 'monthly' ? 'mese' : this.weeklyCadenceLabel(installments);
    }

    return {
      line,
      every,
      lessonsPerPayment,
      amount: first?.amount ?? null,
      startDate: first?.date ?? null,
      installments,
      balanceDueDate: last?.date ?? null,
      downPayment: downRow
    };
  }

  get servicePeriodStart(): any {
    return this.toDatePipeInput(this.formOrEntityValue('field_sale_date'));
  }

  get servicePeriodEnd(): any {
    return this.toDatePipeInput(this.formOrEntityValue('field_expiration_date'));
  }

  /** DatePipe accepts Date/string/number but not Moment, so unwrap it. */
  private toDatePipeInput(v: any): any {
    if (v && typeof v === 'object' && typeof v.toDate === 'function') {
      return v.toDate();
    }
    return v || null;
  }

  /**
   * Weekly plans paying on one weekday have a uniform 7-day gap ("ogni 7
   * giorni"); multi-weekday plans (e.g. Mon & Wed) don't fit "ogni N giorni",
   * so name the actual weekdays instead ("ogni lunedì e mercoledì").
   */
  private weeklyCadenceLabel(installments: { date: string | null }[]): string {
    const days = installments
      .map(r => this.toJsDate(r.date))
      .filter(Boolean) as Date[];
    if (days.length < 2) return '7 giorni';

    const gaps = new Set<number>();
    for (let i = 1; i < days.length; i++) {
      gaps.add(Math.round((days[i].getTime() - days[i - 1].getTime()) / 86400000));
    }
    if (gaps.size === 1) {
      return `${[...gaps][0]} giorni`;
    }

    const weekdayNames = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
    // Distinct weekdays, ordered Monday-first like the Italian week.
    const weekdays = [...new Set(days.map(d => d.getDay()))]
      .sort((x, y) => ((x + 6) % 7) - ((y + 6) % 7))
      .map(d => weekdayNames[d]);
    return weekdays.length === 1
      ? weekdays[0]
      : weekdays.slice(0, -1).join(', ') + ' e ' + weekdays[weekdays.length - 1];
  }

  private toJsDate(v: any): Date | null {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    if (typeof v === 'string') {
      const d = new Date(v.length === 10 ? v + 'T00:00:00' : v);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  /** Scheduled payment rows normalized to {amount, date, down, lessonsRemaining}, date ascending. */
  private scheduledPaymentRows(): { amount: number; date: string | null; down: boolean; lessonsRemaining: number | null }[] {
    let raw = agreementScheduledPayments(this.enrollmentForm?.get?.('field_scheduled_payments')?.value);
    if (raw.length === 0) {
      raw = agreementScheduledPayments(this.entityData?.field_scheduled_payments);
    }

    const rows = raw
      .map(r => {
        const rem = this.fieldValue(r, 'field_number_lesson_remaining');
        return {
          amount: parseFloat(this.fieldValue(r, 'field_payment_amount')) || 0,
          date: this.fieldValue(r, 'field_payment_date') || null,
          down: ['1', 1, true].includes(this.fieldValue(r, 'field_down_payment')),
          lessonsRemaining: (rem === null || rem === undefined || rem === '') ? null : (parseInt(rem, 10) || 0)
        };
      })
      .filter(r => r.amount > 0);

    // Generated rows arrive in order, but saved refs may not, so sort by date,
    // keeping date-less rows (by-the-lesson tail payments) at the end.
    return rows.sort((x, y) => !x.date ? 1 : !y.date ? -1 : x.date.localeCompare(y.date));
  }

  /**
   * Lessons-per-payment for a by-the-lesson plan. The generator stores
   * field_number_lesson_remaining as a countdown BEFORE each payment
   * (e.g. 10, 7, 4, 1 for 3-lesson intervals), so the gap between consecutive
   * values is the interval. Falls back to the 'interval' form control, then to
   * an even split of the lesson count across installments.
   */
  private byLessonInterval(installmentCount: number): number | null {
    const rows = this.scheduledPaymentRows();
    const remaining = rows
      .map(r => r.lessonsRemaining)
      .filter((v): v is number => v !== null && v > 0)
      .sort((a, b) => b - a); // descending: 10, 7, 4, 1
    if (remaining.length >= 2) {
      const gap = remaining[0] - remaining[1];
      if (gap > 0) return gap;
    }

    const interval = parseFloat(this.enrollmentForm?.get?.('interval')?.value);
    if (interval > 0) return interval;

    const lessonCount = parseFloat(this.formOrEntityValue('field_enrollment_lesson_count')) || 0;
    return (lessonCount > 0 && installmentCount > 0) ? Math.ceil(lessonCount / installmentCount) : null;
  }

  /** Reads a field from the live form first, then the saved entity. */
  private formOrEntityValue(fieldName: string): any {
    const formVal = this.enrollmentForm?.get?.(fieldName)?.value;
    if (formVal !== undefined && formVal !== null && formVal !== '') return formVal;
    return this.fieldValue(this.entityData, fieldName);
  }

  /** Unwraps both plain values and Drupal's [{value: ...}] field shape. */
  private fieldValue(obj: any, fieldName: string): any {
    const v = obj?.[fieldName];
    if (Array.isArray(v)) return v[0]?.value ?? v[0];
    if (v && typeof v === 'object' && 'value' in v) return v.value;
    return v;
  }
}
